/**
 * Lumenate review carousel (LUM-30).
 *
 * Infinite in both directions, matching lumenate.co's Swiper (`loop: true`): the real
 * cards are cloned once ahead of and once behind the track, so scrolling off either
 * end just keeps going. Once the scroll settles, the position is normalised by whole
 * copies back into the middle set — invisible, because all three copies are identical.
 * Arrows step one card and are never disabled; a slow timer auto-advances.
 *
 * Horizon's own arrow and drag handlers are suppressed: both resolve positions against
 * the real slides only, so they'd yank the track out of the cloned runway.
 *
 * Opt in per carousel via data-lumenate-autoplay-speed (seconds) on the wrapper.
 */
(() => {
  const SETTLE_MS = 140;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  // Rest a couple of px inside the scroller's clip so sub-pixel rounding of the fractional slide
  // width never shaves the first card's left border. Baked into `origin` so rest, normalize and the
  // arrows all share the same grid; the gap is invisible navy (and sits inside the desktop gutter).
  const EDGE_SAFETY = 2;

  /**
   * Builds an inert, non-referenced copy of every card.
   * @param {HTMLElement[]} cards
   * @returns {DocumentFragment}
   */
  function cloneSet(cards) {
    const fragment = document.createDocumentFragment();

    for (const card of cards) {
      const copy = /** @type {HTMLElement} */ (card.cloneNode(true));
      copy.inert = true;
      copy.setAttribute('aria-hidden', 'true');
      copy.setAttribute('data-carousel-clone', '');

      // Horizon collects slides through [ref] and the theme editor selects them
      // through [data-carousel-card]; ids have to stay unique. A clone is scenery.
      for (const element of [copy, ...copy.querySelectorAll('[ref], [id], [data-carousel-card]')]) {
        element.removeAttribute('ref');
        element.removeAttribute('id');
        element.removeAttribute('data-carousel-card');
      }

      fragment.append(copy);
    }

    return fragment;
  }

  /** @param {HTMLElement} wrapper */
  function setup(wrapper) {
    if (wrapper.dataset.reviewCarousel) return;

    const slideshow = wrapper.querySelector('slideshow-component');
    const track = /** @type {HTMLElement | null} */ (wrapper.querySelector('slideshow-slides'));
    const cards = track ? /** @type {HTMLElement[]} */ ([...track.querySelectorAll(':scope > .card')]) : [];
    if (!slideshow || !track || cards.length < 2) return;

    wrapper.dataset.reviewCarousel = '1';

    // The theme editor morphs blocks in place and resolves selection against the real
    // cards, so leave its DOM alone and fall back to an edge-to-edge jump there.
    const looping = !window.Shopify?.designMode;

    /** Distance between two adjacent cards. Every card is the same width by CSS. */
    let step = cards[1].offsetLeft - cards[0].offsetLeft;
    /** Width of one full copy of the set. */
    let cycle = step * cards.length;
    /** Scroll offset where the real set starts. */
    let origin = 0;

    let clonesAdded = false;

    // Enough copies on each side that no realistic flick reaches the hard end of the
    // track before the position is normalised — normalising mid-scroll would kill the
    // browser's momentum, so it only happens once the scroll has settled. Resting at
    // the start of the real set, `copies` cycles leaves `copies * cycle - clientWidth`
    // of runway ahead, so three viewports of copy buys two viewports of flick.
    const addClones = () => {
      if (clonesAdded || !looping || cycle <= 0) return;
      clonesAdded = true;

      const copies = Math.min(3, Math.ceil((3 * track.clientWidth) / cycle));
      for (let i = 0; i < copies; i++) {
        track.prepend(cloneSet(cards));
        track.append(cloneSet(cards));
      }
    };

    const measure = () => {
      const paddingStart = parseFloat(getComputedStyle(track).paddingInlineStart) || 0;
      step = cards[1].offsetLeft - cards[0].offsetLeft;
      cycle = step * cards.length;
      // Desktop only: the safety gap hides inside the gutter there, but on mobile the first card
      // must line up exactly with the page margin, so no nudge below 750px.
      const edgeSafety = window.matchMedia('(min-width: 750px)').matches ? EDGE_SAFETY : 0;
      origin = looping ? cards[0].offsetLeft - paddingStart - edgeSafety : 0;
    };

    /** Pending programmatic destination, so rapid clicks compose instead of colliding. */
    let target = null;
    /** @type {{ x: number, left: number, moved: boolean } | null} */
    let drag = null;

    const scrollTo = (left, instant) =>
      track.scrollTo({ left, behavior: instant || reducedMotion.matches ? 'instant' : 'smooth' });

    /**
     * Pulls the position back into the real set once a whole cycle out. Every copy is
     * identical, so a whole-cycle jump shows exactly the same pixels.
     */
    const normalize = () => {
      if (!looping || cycle <= 0 || drag) return;

      const current = track.scrollLeft;
      const wrapped = origin + ((((current - origin) % cycle) + cycle) % cycle);

      if (Math.abs(wrapped - current) > 1) scrollTo(wrapped, true);
    };

    /** @param {number} direction */
    const stepBy = (direction) => {
      const from = target ?? track.scrollLeft;
      // Snap to the card grid. After a partial drag the track rests between two cards; align to
      // the next boundary in the pressed direction rather than adding a raw step to the offset,
      // which would carry the misalignment forward. The epsilon absorbs sub-pixel drift so an
      // already-aligned position still advances a whole card instead of snapping back to itself.
      const rel = step > 0 ? (from - origin) / step : 0;
      const index = direction > 0 ? Math.floor(rel + 1e-3) + 1 : Math.ceil(rel - 1e-3) - 1;
      let next = origin + index * step;

      if (!looping) {
        // Editor fallback: no clones to slide into. Rest against the edge first, then
        // jump to the far one, so no card is skipped on the way round.
        const max = track.scrollWidth - track.clientWidth;
        if (next > max) next = from >= max - 1 ? 0 : max;
        else if (next < 0) next = from <= 1 ? max : 0;
      }

      target = next;
      scrollTo(next);
    };

    const wire = (selector, direction) => {
      const button = wrapper.querySelector(selector);
      if (!button) return;

      // Drop Horizon's delegated handler; its page-jump ignores the cloned runway.
      button.removeAttribute('on:click');
      button.disabled = false;
      button.addEventListener('click', (event) => {
        event.preventDefault();
        stepBy(direction);
      });
    };
    wire('.slideshow-control--next', 1);
    wire('.slideshow-control--previous', -1);

    let settleTimer;
    track.addEventListener(
      'scroll',
      () => {
        clearTimeout(settleTimer);
        settleTimer = setTimeout(() => {
          target = null;
          normalize();
        }, SETTLE_MS);
      },
      { passive: true }
    );

    // The editor tears sections down and rebuilds them, so every listener that outlives this
    // element — the document-level drag handlers below, and the autoplay wiring further down —
    // has to retire with the wrapper, or a detached track keeps being driven.
    const teardown = new AbortController();

    // Horizon's drag finishes by snapping to one of the real slides, so suppress it at
    // the ancestor (its listener sits on the scroller) and run a plain one instead.
    slideshow.addEventListener('mousedown', (event) => event.stopPropagation(), { capture: true });

    track.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      drag = { x: event.clientX, left: track.scrollLeft, moved: false };
      target = null;
    });

    document.addEventListener('pointermove', (event) => {
      if (!drag) return;

      const delta = event.clientX - drag.x;
      if (!drag.moved) {
        if (Math.abs(delta) < 4) return;
        drag.moved = true;
        slideshow.setAttribute('dragging', '');
      }

      scrollTo(drag.left - delta, true);
    }, { signal: teardown.signal });

    const endDrag = () => {
      if (!drag) return;

      const { moved } = drag;
      drag = null;
      slideshow.removeAttribute('dragging');
      // Swallow the click that closes a drag so cards don't activate under it.
      if (moved) document.addEventListener('click', (event) => event.preventDefault(), { once: true, capture: true });
      normalize();
    };
    document.addEventListener('pointerup', endDrag, { signal: teardown.signal });
    document.addEventListener('pointercancel', endDrag, { signal: teardown.signal });

    measure();

    const seconds = parseInt(wrapper.getAttribute('data-lumenate-autoplay-speed'), 10) || 5;
    /** @type {number | undefined} */
    let timer;

    // A carousel below the fold used to advance anyway: measured 8416 -> 5656 scrollLeft over
    // 13s while 3375px out of view, so a visitor scrolling down to it found it parked on an
    // arbitrary card, and every step ran a smooth scroll plus normalize() for nobody.
    let onScreen = false;

    const startAutoplay = () => {
      if (timer || reducedMotion.matches || !onScreen) return;
      timer = setInterval(() => {
        if (document.hidden || drag || wrapper.matches(':hover, :focus-within')) return;
        stepBy(1);
      }, seconds * 1000);
    };

    const stopAutoplay = () => {
      clearInterval(timer);
      timer = undefined;
    };

    /**
     * Brings the loop up once the geometry is known.
     *
     * There is no "nothing to cycle" case: live rotates its review carousel even when the
     * cards already fill the track - three quotes in three columns on the PDP and the Nova
     * page, `slidesPerView: 3` with `loop: true` and a 4s autoplay - so the arrows are always
     * live and the timer always runs. An earlier version suppressed both here on the grounds
     * that cycling three of three only shuffles identical pixels; it also left desktop static
     * where live moves.
     */
    const sync = () => {
      if (!clonesAdded) {
        addClones();
        measure();
        scrollTo(origin, true);
      }

      startAutoplay();
    };

    sync();

    const resizeObserver = new ResizeObserver(() => {
      measure();
      sync();
      // Re-align to the start on any layout change (viewport resize, late web-font load). The cards
      // resize but scrollLeft does not, which otherwise leaves the row resting off-grid and clips the
      // edge cards; origin is the balanced rest position (EDGE_SAFETY of slack each side).
      scrollTo(origin, true);
    });
    resizeObserver.observe(track);

    // Only cycle while the carousel is on screen. rootMargin keeps it running through the small
    // overscroll either side rather than stopping and starting at the exact edge.
    const visibility = new IntersectionObserver(
      ([entry]) => {
        onScreen = entry.isIntersecting;
        if (onScreen) startAutoplay();
        else stopAutoplay();
      },
      { rootMargin: '100px' }
    );
    visibility.observe(wrapper);

    reducedMotion.addEventListener('change', () => (reducedMotion.matches ? stopAutoplay() : startAutoplay()), {
      signal: teardown.signal,
    });

    // The pointer handlers below live on `document`, so they outlive the wrapper unless they are
    // told to retire with it; the observers hold the track and wrapper themselves.
    document.addEventListener(
      'shopify:section:unload',
      (event) => {
        if (!(event.target instanceof HTMLElement) || !event.target.contains(wrapper)) return;
        stopAutoplay();
        resizeObserver.disconnect();
        visibility.disconnect();
        teardown.abort();
      },
      { signal: teardown.signal }
    );
  }

  function init() {
    document.querySelectorAll('[data-lumenate-autoplay-speed]').forEach(setup);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  setTimeout(init, 1500);
  document.addEventListener('shopify:section:load', init);
})();
